import { motion, MotionConfig, type Variants } from 'framer-motion';
import { WHATSAPP_URL } from '../../constants';
// Logo do título "Cris Morais — Arquitetura para Crianças". Troque aqui se necessário.
import heroLogo from '../../assets/figma/page13/hero-logo.png';

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13, delayChildren: 0.05 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const photoReveal: Variants = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const doodle: Variants = {
  hidden: { opacity: 0, scale: 0.4, rotate: -12 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: { type: 'spring', stiffness: 260, damping: 16 },
  },
};

interface HeroProps {
  /** Foto da arquiteta (PNG recortado, figura inteira), usada no desktop.
      Troque em /public/images/cris-morais.png */
  photoSrc?: string;
  /** Versão mobile da foto, sem a faixa esquerda que o design esconde
      (gerada a partir da foto completa cortando 37/130 da largura). Se trocar,
      regere o polygon() do shape-outside com scripts/gen-hero-shape.py e
      confira o aspect-[w/h] do float e o min() do spacer. */
  photoMobileSrc?: string;
  /** Rabisco "carinha" azul (aparece só no mobile). Troque em /public/rosto-azul.svg */
  faceDoodleSrc?: string;
  /** Rabisco "laço" azul (aparece só no mobile). Troque em /public/laco-azul.svg */
  ribbonDoodleSrc?: string;
  whatsappUrl?: string;
}

export function Hero({
  photoSrc = '/images/cris-morais.png',
  photoMobileSrc = '/images/cris-morais-mobile.png',
  faceDoodleSrc = '/rosto-azul.svg',
  ribbonDoodleSrc = '/laco-azul.svg',
  whatsappUrl = WHATSAPP_URL,
}: HeroProps) {
  return (
    // reducedMotion="user": com "reduzir movimento" ativo no sistema do visitante,
    // o framer anima só opacidade (sem deslocamento/escala/rotação)
    <MotionConfig reducedMotion="user">
    <section
      id="inicio"
      className="relative min-h-[var(--hero-h)] overflow-hidden bg-[var(--color-creme)] pt-[72px] [--hero-h:clamp(700px,calc(820px_-_12vw),820px)] [--wave-h:48px] md:grid md:min-h-0 md:grid-cols-[1fr_1.1fr] md:items-stretch md:pt-0 md:pb-[30px]"
    >
      {/* Spacer flutuante de largura zero: empurra o float da foto para baixo
          via clear (não usar margin-top no float da foto — o Safari/WebKit
          ancora o shape-outside na margin box e a silhueta descola da foto) */}
      <div
        aria-hidden="true"
        className="float-left h-[calc(var(--hero-h)_-_var(--wave-h)_-_min(129.8vw,559px))] w-0 md:hidden"
      />
      {/* Coluna da foto — mobile: usa a versão recortada da foto (o sangramento
          esquerdo do design já vem cortado no arquivo), flutuando na posição
          natural de fluxo, base colada na onda; o texto contorna a silhueta via
          shape-outside com polygon() + border-box. O box tem aspect-ratio
          idêntico ao PNG mobile (455/635) para o polígono casar com a foto em
          qualquer viewport. NÃO usar margens, position ou shape-outside:url()
          neste float: os motores divergem no posicionamento da shape (WebKit
          ancora na margin box; Chromium diverge com position:relative) e o
          texto vaza por cima da foto em produção. Se trocar a foto, regere os
          pontos com scripts/gen-hero-shape.py.
          Desktop: coluna esquerda do grid, ancorada embaixo */}
      <motion.div
        className="float-left clear-left aspect-[455/635] w-[min(93%,400.6px)] [shape-margin:1rem] [shape-outside:polygon(0%_0%,21.1%_1.3%,31.4%_6.9%,37.4%_19.5%,40.2%_21.4%,42.2%_26.5%,41.1%_29.6%,51.2%_37.2%,52.7%_46%,57.8%_56.1%,63.5%_61.7%,76.3%_66.8%,80%_73.1%,85.5%_77.5%,85.3%_83.1%,92.7%_92%,92.7%_100%,0%_100%)_border-box] md:float-none md:clear-none md:flex md:aspect-auto md:h-auto md:w-auto md:items-end"
        variants={photoReveal}
        initial="hidden"
        animate="visible"
      >
        <picture className="contents">
          {/* mobile: recorte sem o sangramento esquerdo; desktop: foto completa */}
          <source media="(max-width: 767px)" srcSet={photoMobileSrc} />
          <img
            src={photoSrc}
            alt="Cris Morais, arquiteta especializada em ambientes infantis"
            className="block h-full w-full object-contain object-left-bottom md:max-h-[800px]"
          />
        </picture>
      </motion.div>

      {/* Coluna de conteúdo — mobile: texto à direita contornando a foto flutuante */}
      {/* No mobile a coluna fica static para o CTA/rabiscos (absolute) se ancorarem
          na seção, logo acima da onda */}
      <motion.div
        className="z-[1] pt-5 pr-4 text-right md:relative md:mb-[50px] md:flex md:flex-col md:justify-center md:gap-6 md:pt-12 md:pr-16 md:pb-8 md:pl-8"
        variants={container}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="mb-4 md:mb-0 md:flex md:flex-col md:items-end md:gap-1" variants={item}>
          <h1 className="m-0 leading-[0]">
            <img
              src={heroLogo}
              alt="Cris Morais - Arquitetura para Crianças"
              className="inline-block w-[min(66vw,260px)] md:block md:w-[min(560px,100%)]"
            />
          </h1>
        </motion.div>

        <motion.p
          className="mb-4 text-[0.78rem] leading-[1.45] text-[var(--color-marinho)] md:mb-0 md:self-end md:text-[clamp(1rem,1vw+0.5rem,1.25rem)] md:leading-[1.75]"
          variants={item}
        >
          Eu sou Cris Morais. Sou arquiteta, mãe, criativa e apaixonada por transformar o universo
          infantil por meio da arquitetura. Escolhi dedicar minha trajetória a criar ambientes que
          unem funcionalidade, magia e emoção, respeitando o desenvolvimento e os sonhos das
          crianças. Trabalho com dedicação e proximidade, criando projetos que traduzem histórias e
          realizam sonhos. Cada ambiente que idealizamos carrega trocas, escuta, aprendizado e a
          missão de impactar positivamente a vida das crianças e todos à sua volta.
        </motion.p>

        <motion.p
          className="mb-4 text-[0.74rem] leading-[1.4] text-[var(--color-marinho)] opacity-65 md:mb-0 md:self-end md:text-[clamp(1rem,1.5vw,1.375rem)] md:leading-[1.65]"
          variants={item}
        >
          Criamos ambientes que inspiram, transformam e acompanham o desenvolvimento das crianças
          com criatividade, sensibilidade e funcionalidade.
        </motion.p>

        {/* Wrapper de altura zero: ancora o CTA logo abaixo do fim do texto no
            mobile (blocos não desviam do float); no desktop some (contents) e o
            CTA volta a ser item da coluna flex */}
        <div className="relative h-0 md:contents">
          <motion.a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-3 right-0 z-[5] inline-flex min-h-[44px] items-center justify-center rounded-[100px] bg-[var(--color-laranja)] px-4 py-2 text-xs whitespace-nowrap text-white transition-colors duration-200 hover:bg-[#b03f19] md:static md:min-h-0 md:self-end md:px-7 md:py-3.5 md:text-base"
            variants={item}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            Fale Comigo no&nbsp;<strong className="font-bold">WhatsApp</strong>
          </motion.a>
        </div>

        {/* Rabiscos decorativos — carinha acima do laço, sobre a lateral direita da
            foto, logo acima da onda (só no mobile); entram com "pop" na sequência
            do stagger, depois do CTA */}
        <motion.img
          src={faceDoodleSrc}
          alt=""
          aria-hidden="true"
          className="absolute right-8 bottom-[120px] z-[5] w-24 md:hidden"
          variants={doodle}
        />
        <motion.img
          src={ribbonDoodleSrc}
          alt=""
          aria-hidden="true"
          className="absolute right-0 bottom-[30px] z-[5] w-48 md:hidden"
          variants={doodle}
        />
      </motion.div>

      {/* Onda decorativa magenta de fim de seção (troque em /public/wave.svg) */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 z-[2] h-[var(--wave-h)] w-full bg-[url('/wave.svg')] bg-cover bg-center bg-no-repeat md:h-20"
      />
    </section>
    </MotionConfig>
  );
}
